// src/errors.ts
var ERROR_CODES = ["VALIDATION", "UNAUTHORIZED", "FORBIDDEN", "NOT_FOUND", "CONFLICT", "NOT_ENTITLED", "INVALID_CREDENTIALS", "ACCOUNT_LOCKED", "RATE_LIMITED", "INTERNAL"];

// src/auth.ts
import { z } from "zod";
var PLATFORM_ROLES = ["super_admin", "platform_staff"];
var loginSchema = z.strictObject({
  username: z.string().trim().min(1).max(60),
  password: z.string().min(1).max(200)
});
var refreshSchema = z.strictObject({ refreshToken: z.string().min(20).max(400) });
var changePasswordSchema = z.strictObject({
  currentPassword: z.string().min(1).max(200),
  /** at least 10 characters, with a letter and a digit */
  newPassword: z.string().min(10).max(200).regex(/[A-Za-z]/, "needs a letter").regex(/[0-9]/, "needs a digit")
});

// src/clients.ts
import { z as z2 } from "zod";
var CLIENT_STATES = ["testing", "live", "suspended", "closed"];
var clientCodeSchema = z2.string().trim().toUpperCase().regex(/^[A-Z][A-Z0-9]{1,9}$/, "2\u201310 letters or digits, starting with a letter");
var clientSlugSchema = z2.string().trim().toLowerCase().regex(/^[a-z0-9]([a-z0-9-]{0,38}[a-z0-9])?$/, "lower-case letters, digits and hyphens");
var CLIENT_INACTIVE_DAYS = 7;
var CLIENT_TIMEZONES = ["Asia/Phnom_Penh", "Asia/Bangkok", "Asia/Ho_Chi_Minh"];
var companyUsernameSchema = z2.string().trim().toLowerCase().regex(/^[a-z][a-z0-9._-]{2,39}$/, "3\u201340 lower-case letters, digits, dot, dash or underscore, starting with a letter");
var text = (max) => z2.string().trim().min(1).max(max);
var clientCreateSchema = z2.strictObject({
  code: clientCodeSchema,
  slug: clientSlugSchema,
  nameEn: text(80),
  nameKm: text(80),
  timezone: z2.enum(CLIENT_TIMEZONES).default("Asia/Phnom_Penh"),
  contactName: text(80).optional(),
  contactPhone: text(30).optional(),
  contactEmail: z2.string().trim().email().max(120).optional(),
  notes: z2.string().trim().max(1e3).optional(),
  packageCode: z2.string().trim().toUpperCase(),
  /** the starter template (its products are installed, its settings are the template layer); null = start with nothing */
  templateCode: z2.string().trim().toUpperCase().nullable(),
  admin: z2.strictObject({ username: companyUsernameSchema, displayName: text(80) })
});
var clientPreviewSchema = z2.strictObject({ packageCode: z2.string().trim().toUpperCase(), templateCode: z2.string().trim().toUpperCase().nullable() });
var PRICE_CURRENCIES = ["USD", "KHR", "THB"];
var PRICE_PERIODS = ["month", "year", "once"];
var priceSchema = z2.strictObject({ amount: z2.number().min(0).max(1e9), currency: z2.enum(PRICE_CURRENCIES), period: z2.enum(PRICE_PERIODS) });
var clientUpdateSchema = z2.strictObject({
  /** the code and the address name can change too (the automatic address moves with it) */
  code: clientCodeSchema.optional(),
  slug: clientSlugSchema.optional(),
  nameEn: text(80).optional(),
  nameKm: text(80).optional(),
  timezone: z2.enum(CLIENT_TIMEZONES).optional(),
  contactName: text(80).nullable().optional(),
  contactPhone: text(30).nullable().optional(),
  contactEmail: z2.string().trim().email().max(120).nullable().optional(),
  notes: z2.string().trim().max(1e3).nullable().optional()
}).refine((v) => Object.keys(v).length > 0, "nothing to change");
var CLIENT_TRANSITIONS = { testing: ["live", "closed"], live: ["suspended", "closed"], suspended: ["live", "closed"], closed: [] };
var clientStateSchema = z2.strictObject({ state: z2.enum(CLIENT_STATES), reason: z2.string().trim().max(300).optional(), confirmCode: z2.string().trim().max(10).optional() });
var clientPriceSchema = z2.strictObject({ price: priceSchema.nullable() });
var companyUserUpdateSchema = z2.strictObject({ displayName: z2.string().trim().min(1).max(80).optional(), username: companyUsernameSchema.optional(), position: z2.string().trim().max(60).nullable().optional(), status: z2.enum(["active", "disabled"]).optional() }).refine((v) => Object.keys(v).length > 0, "nothing to change");

// src/audit.ts
var AUDIT_ACTIONS = ["auth.login", "auth.login_failed", "auth.logout", "auth.password_change", "client.create", "client.update", "client.state_change", "product.create", "product.status", "product.rename", "product.version_draft", "product.version_update", "product.version_publish", "product.version_delete", "product.exception_add", "product.exception_remove", "client_product.install", "client_product.move", "client_product.remove", "client_product.rename", "package.create", "package.update", "package.version_draft", "package.version_update", "package.version_publish", "package.version_delete", "client.package", "company_user.create", "client.price", "area.update", "area.delete", "domain.update", "domain.delete", "pattern.create", "pattern.update", "domain.create", "domain.verify", "domain.status", "release.create", "release.update", "release.publish", "release.withdraw", "company_user.update", "company_auth.login", "company_auth.login_failed", "company_auth.logout", "company_auth.password_change", "level.create", "level.update", "level.delete", "area.create", "account.create", "account.update", "account.deal", "company_user.password_reset", "client.template", "client.setting_override", "client.setting_reset", "template.create", "template.update", "template.version_draft", "template.version_update", "template.version_publish", "template.version_delete", "result.enter", "result.publish", "result.amend", "ticket.sell", "ticket.cancel", "ticket.settle", "system.seed"];

// src/products.ts
import { BET_MODES } from "@lp/engine";
import { z as z3 } from "zod";
var PRODUCT_CODE = /^[A-Z][A-Z0-9]{0,9}$/;
var WEEKDAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
var PATTERN_CODE = /^[a-z][a-z0-9-]{1,31}$/;
var hhmm = z3.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "HH:MM");
var postCode = z3.string().regex(/^[A-Za-z0-9]{1,4}$/, "1\u20134 letters or digits");
var name = z3.string().trim().min(1).max(80);
var closeTimeSchema = z3.union([hhmm, z3.strictObject(Object.fromEntries(WEEKDAYS.map((d) => [d, hhmm])))]);
var drawSchema = z3.strictObject({
  /** the draw time, in the product's time zone */
  time: hhmm,
  /** posts this draw offers; absent = every post of the product */
  posts: z3.array(postCode).min(1).optional(),
  /** when betting closes, per group of posts; a post not listed closes at the game's default */
  closes: z3.array(z3.strictObject({ posts: z3.array(postCode).min(1), at: closeTimeSchema })).default([]),
  /** this draw's own result pattern, when it differs from the game's (null = every post typed by hand) */
  rule: z3.string().regex(PATTERN_CODE).nullable().optional(),
  /**
   * this draw's result is shaped exactly like another draw's (owner 2026-09-22: TN Old 10:25 = Thinh Nam 10:30 …): it always uses that
   * draw's pattern, so when that one is set or changed, this one follows — nothing copied
   */
  resultAs: z3.strictObject({ product: z3.string().regex(PRODUCT_CODE), game: z3.string().regex(/^[A-Z][A-Z0-9]{0,11}$/), time: hhmm }).optional()
});
var configGapSchema = z3.strictObject({
  game: z3.string().regex(/^[A-Z][A-Z0-9]{0,11}$/),
  /** one draw, or the whole game */
  time: hhmm.optional(),
  what: z3.string().trim().min(3).max(300),
  /** where the answer will come from (lo855, the owner, an official page) */
  source: z3.string().trim().max(120).optional()
});
var scheduleSchema = z3.discriminatedUnion("kind", [
  /** these weekdays (0 = Sunday); all seven = every day */
  z3.strictObject({ kind: z3.literal("weekly"), days: z3.array(z3.number().int().min(0).max(6)).min(1) }),
  /** these dates of every month (TH: 1 and 16) */
  z3.strictObject({ kind: z3.literal("monthly"), dates: z3.array(z3.number().int().min(1).max(31)).min(1) })
]);
var perDigits = z3.strictObject({ "2": z3.number().positive().max(1e5).optional(), "3": z3.number().positive().max(1e5).optional() });
var bettingSchema = z3.strictObject({
  /** cost multiplier per post and digit length; a post not listed costs ×1 */
  multipliers: z3.record(postCode, perDigits).default({}),
  /** post + digit length combinations that cannot be bet (VN 6:30: A4 3D) */
  refused: z3.array(z3.strictObject({ post: postCode, digits: z3.union([z3.literal(2), z3.literal(3)]) })).default([]),
  /** what one winning number pays, × the amount bet on it (2D ×90, 3D ×800 — the earlier system's default, to confirm) */
  payout: z3.strictObject({ "2": z3.number().positive().max(1e5), "3": z3.number().positive().max(1e5) }).default({ "2": 90, "3": 800 }),
  /** a post that pays differently */
  payoutByPost: z3.record(postCode, perDigits).default({}),
  modes: z3.array(z3.enum(BET_MODES)).min(1).default([...BET_MODES]),
  /** amount per number, in the client's minor unit (riel) */
  minAmount: z3.number().int().min(1).default(100),
  amountStep: z3.number().int().min(1).default(50),
  maxAmountPerNumber: z3.number().int().min(1).default(5e6),
  /** numbers one roll may produce (0 = no limit) */
  maxRollNumbers: z3.number().int().min(0).max(1e3).default(0)
});
var DEFAULT_BETTING = bettingSchema.parse({});
var gameSchema = z3.strictObject({
  code: z3.string().regex(/^[A-Z][A-Z0-9]{0,11}$/),
  nameEn: name,
  nameKm: name,
  schedule: scheduleSchema,
  draws: z3.array(drawSchema).min(1),
  /** a post without a listed close closes this many minutes before the draw (a default — shown as "to confirm") */
  defaultCloseMinutes: z3.number().int().min(0).max(240).default(5),
  /** the result pattern (Phase 3); null = every post of the draw is typed by hand as a 2D and a 3D number (Dragon 5D) */
  rule: z3.string().regex(PATTERN_CODE).nullable().default(null),
  /** what a bet costs and pays (Phase 4); absent in older versions = the defaults */
  betting: bettingSchema.default(DEFAULT_BETTING)
});
var productConfigSchema = z3.strictObject({
  /** IANA time zone the draw times are in (TH: Asia/Bangkok) */
  timezone: z3.string().min(3).max(40),
  /** bet digits: 2D and 3D (Dragon 5D included — owner 2026-09-21) */
  digits: z3.array(z3.union([z3.literal(2), z3.literal(3)])).min(1).default([2, 3]),
  posts: z3.array(z3.strictObject({ code: postCode, kind: z3.enum(["single", "multi"]).default("single") })).min(1),
  games: z3.array(gameSchema).min(1),
  /** what is not confirmed yet (see configGapSchema) */
  gaps: z3.array(configGapSchema).max(100).default([])
}).superRefine((c, ctx) => {
  const posts = new Set(c.posts.map((p) => p.code));
  if (posts.size !== c.posts.length) ctx.addIssue({ code: "custom", path: ["posts"], message: "a post is listed twice" });
  try {
    new Intl.DateTimeFormat("en", { timeZone: c.timezone });
  } catch {
    ctx.addIssue({ code: "custom", path: ["timezone"], message: "not a known time zone" });
  }
  const games = /* @__PURE__ */ new Set();
  c.games.forEach((g, gi) => {
    if (games.has(g.code)) ctx.addIssue({ code: "custom", path: ["games", gi, "code"], message: "a game code is used twice" });
    games.add(g.code);
    const named = [...Object.keys(g.betting.multipliers), ...Object.keys(g.betting.payoutByPost), ...g.betting.refused.map((r) => r.post)];
    for (const p of named) if (!posts.has(p)) ctx.addIssue({ code: "custom", path: ["games", gi, "betting"], message: `post ${p} is not a post of the product` });
    if (g.betting.minAmount > g.betting.maxAmountPerNumber) ctx.addIssue({ code: "custom", path: ["games", gi, "betting", "minAmount"], message: "the minimum is above the maximum" });
    const times = /* @__PURE__ */ new Set();
    g.draws.forEach((d, di) => {
      if (times.has(d.time)) ctx.addIssue({ code: "custom", path: ["games", gi, "draws", di, "time"], message: "two draws at the same time" });
      times.add(d.time);
      const offered = new Set(d.posts ?? c.posts.map((p) => p.code));
      for (const p of d.posts ?? []) if (!posts.has(p)) ctx.addIssue({ code: "custom", path: ["games", gi, "draws", di, "posts"], message: `post ${p} is not a post of the product` });
      if (d.rule !== void 0 && d.resultAs) ctx.addIssue({ code: "custom", path: ["games", gi, "draws", di, "resultAs"], message: "a draw has its own pattern or follows another draw \u2014 not both" });
      const closed = /* @__PURE__ */ new Set();
      d.closes.forEach((cl, ci) => {
        for (const p of cl.posts) {
          if (!offered.has(p)) ctx.addIssue({ code: "custom", path: ["games", gi, "draws", di, "closes", ci], message: `post ${p} is not offered by this draw` });
          if (closed.has(p)) ctx.addIssue({ code: "custom", path: ["games", gi, "draws", di, "closes", ci], message: `post ${p} has two close times` });
          closed.add(p);
        }
      });
    });
  });
  c.gaps.forEach((gap, i) => {
    const g = c.games.find((x) => x.code === gap.game);
    if (!g) ctx.addIssue({ code: "custom", path: ["gaps", i, "game"], message: `no game ${gap.game}` });
    else if (gap.time && !g.draws.some((d) => d.time === gap.time)) ctx.addIssue({ code: "custom", path: ["gaps", i, "time"], message: `${gap.game} has no draw at ${gap.time}` });
  });
});
var PRODUCT_STATUSES = ["active", "inactive"];
var VERSION_STATUSES = ["draft", "published"];
var DRAW_EXCEPTION_ACTIONS = ["cancel", "move", "extra"];
var drawExceptionSchema = z3.strictObject({
  gameCode: z3.string().regex(/^[A-Z][A-Z0-9]{0,11}$/),
  /** the normal draw date (cancel, move) or the extra date (extra) */
  date: z3.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  action: z3.enum(DRAW_EXCEPTION_ACTIONS),
  /** move: the new date (and time; absent = the usual time) */
  toDate: z3.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  time: hhmm.optional(),
  note: z3.string().trim().max(200).optional()
}).refine((e) => e.action !== "move" || !!e.toDate, { path: ["toDate"], message: "a moved draw needs its new date" }).refine((e) => e.action !== "extra" || !!e.time, { path: ["time"], message: "an extra draw needs its time" });
var productVersionUpdateSchema = z3.strictObject({ config: productConfigSchema, note: z3.string().trim().max(300).optional() });
var productPublishSchema = z3.strictObject({ confirmCode: z3.string().trim().min(1).max(10) });
var productStatusSchema = z3.strictObject({ status: z3.enum(PRODUCT_STATUSES) });
var productUpdateSchema = z3.strictObject({ nameEn: name.optional(), nameKm: name.optional(), status: z3.enum(PRODUCT_STATUSES).optional() }).refine((v) => Object.keys(v).length > 0, "nothing to change");
var CLIENT_PRODUCT_STATUSES = ["active", "removed"];
var clientProductInstallSchema = z3.strictObject({
  productCode: z3.string().regex(PRODUCT_CODE),
  version: z3.number().int().min(1).optional(),
  /** the client's own name for the product; left out = the platform's name */
  nameEn: name.optional(),
  nameKm: name.optional()
});
var clientProductRenameSchema = z3.strictObject({ nameEn: name.nullable(), nameKm: name.nullable() });
var clientProductMoveSchema = z3.strictObject({ version: z3.number().int().min(1) });

// src/results.ts
import { z as z4 } from "zod";
var RESULT_STATES = ["none", "draft", "published"];
var digits = (n) => z4.string().regex(new RegExp(`^\\d{${n}}$`), `${n} digits`);
var resultInputSchema = z4.strictObject({
  /** role → slot key → values, in the slot's order */
  roles: z4.record(z4.string().max(12), z4.record(z4.string().max(24), z4.array(z4.string().max(12)).max(100))).default({}),
  /** hand-typed posts: its 2D and 3D number */
  hand: z4.record(z4.string().max(4), z4.strictObject({ d2: digits(2), d3: digits(3) })).default({})
});
var resultAmendSchema = z4.strictObject({ inputs: resultInputSchema, reason: z4.string().trim().min(3).max(300) });
var resultPasteSchema = z4.strictObject({ text: z4.string().min(10).max(2e4) });
var patternTrySchema = z4.strictObject({ roles: resultInputSchema.shape.roles, weekday: z4.number().int().min(0).max(6).optional() });
var RESULT_SOURCES = ["manual", "paste"];
var resultSaveSchema = z4.strictObject({ inputs: resultInputSchema, source: z4.enum(RESULT_SOURCES).default("manual") });
var patternCreateSchema = z4.strictObject({
  code: z4.string().trim().toLowerCase().regex(/^[a-z][a-z0-9-]{1,31}$/, "2\u201332 lower-case letters, digits or -"),
  nameEn: z4.string().trim().min(1).max(80),
  nameKm: z4.string().trim().min(1).max(80),
  description: z4.string().trim().max(400).optional(),
  /** the rule (the engine's rule language) — or copy one */
  dsl: z4.unknown().optional(),
  copyOf: z4.string().trim().max(32).optional()
});
var patternUpdateSchema = z4.strictObject({ nameEn: z4.string().trim().min(1).max(80).optional(), nameKm: z4.string().trim().min(1).max(80).optional(), description: z4.string().trim().max(400).nullable().optional(), dsl: z4.unknown().optional() }).refine((v) => Object.keys(v).length > 0, "nothing to change");

// src/tickets.ts
import { BET_MODES as BET_MODES2 } from "@lp/engine";
import { z as z5 } from "zod";
var postCode2 = z5.string().regex(/^[A-Za-z0-9]{1,4}$/);
var betLineSchema = z5.strictObject({
  mode: z5.enum(BET_MODES2),
  /** the number as typed (roll: the start; wildcards W) */
  input1: z5.string().trim().min(1).max(40),
  /** roll: the end number; multiply of a roll: the roll */
  input2: z5.string().trim().max(40).nullish(),
  digitLen: z5.union([z5.literal(2), z5.literal(3)]),
  postCodes: z5.array(postCode2).min(1).max(20),
  /** amount on each number, in the client's minor unit (riel) */
  amountPerNumber: z5.number().int().positive()
});
var slipSchema = z5.strictObject({
  product: z5.string().regex(/^[A-Za-z][A-Za-z0-9]{0,9}$/).transform((s) => s.toUpperCase()),
  game: z5.string().regex(/^[A-Za-z][A-Za-z0-9]{0,11}$/).transform((s) => s.toUpperCase()),
  /** the draw's date and time in the product's time zone */
  date: z5.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  time: z5.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  lines: z5.array(betLineSchema).min(1).max(200)
});
var sellSchema = z5.strictObject({ ...slipSchema.shape, idempotencyKey: z5.string().uuid(), accountId: z5.number().int().min(1).optional() });
var TICKET_STATUSES = ["open", "won", "lost", "cancelled"];
var TICKET_CHANNELS = ["console", "app", "web", "data_entry", "api"];
var ticketCancelSchema = z5.strictObject({ reason: z5.string().trim().min(3).max(200) });
var ticketListQuery = z5.object({ date: z5.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(), product: z5.string().max(10).optional(), limit: z5.coerce.number().int().min(1).max(200).default(100) });

// src/packages.ts
import { z as z7 } from "zod";

// src/settings.ts
import { z as z6 } from "zod";
var SETTING_SOURCES = ["platform", "package", "template", "company"];
var SETTINGS = [
  {
    key: "sale.closeEarlierMinutes",
    scope: "product",
    kind: "int",
    min: 0,
    max: 120,
    default: 0,
    label: "Stop sales earlier (minutes)",
    help: "Sales close this many minutes before the product\u2019s close time of each post"
  },
  {
    key: "sale.maxPerNumber",
    scope: "product",
    kind: "int",
    min: 100,
    max: 1e9,
    default: null,
    label: "Most per number (riel)",
    help: "The largest amount on one number; empty = the product\u2019s own rule"
  },
  { key: "payout.2d", scope: "product", kind: "int", min: 1, max: 1e5, default: null, label: "Payout 2D (\xD7)", help: "What one winning 2D number pays, \xD7 the amount; empty = the product\u2019s own rule", feature: "rules.custom" },
  { key: "payout.3d", scope: "product", kind: "int", min: 1, max: 1e5, default: null, label: "Payout 3D (\xD7)", help: "What one winning 3D number pays, \xD7 the amount; empty = the product\u2019s own rule", feature: "rules.custom" },
  { key: "ticket.footer", scope: "client", kind: "text", maxLength: 120, default: "", label: "Ticket footer", help: "A line printed under every ticket" }
];
var settingDef = (key) => SETTINGS.find((s) => s.key === key);
function settingProblem(def, value) {
  if (def.kind === "int") {
    if (typeof value !== "number" || !Number.isInteger(value)) return "a whole number";
    if (def.min != null && value < def.min) return `at least ${def.min}`;
    if (def.max != null && value > def.max) return `at most ${def.max}`;
    return null;
  }
  if (typeof value !== "string") return "a text";
  if (def.maxLength != null && value.length > def.maxLength) return `at most ${def.maxLength} characters`;
  return null;
}
function splitSettingKey(k) {
  const at = k.indexOf("@");
  return at < 0 ? { key: k, product: null } : { key: k.slice(0, at), product: k.slice(at + 1) };
}
var settingsMapSchema = z6.record(z6.string().max(80), z6.union([z6.number(), z6.string().max(400)])).superRefine((map, ctx) => {
  for (const [k, v] of Object.entries(map)) {
    const { key, product } = splitSettingKey(k);
    const def = settingDef(key);
    if (!def) {
      ctx.addIssue({ code: "custom", path: [k], message: `no setting ${key}` });
      continue;
    }
    if (product != null && (def.scope !== "product" || !PRODUCT_CODE.test(product))) ctx.addIssue({ code: "custom", path: [k], message: def.scope === "product" ? `not a product code: ${product}` : `${key} is not a product setting` });
    const problem = settingProblem(def, v);
    if (problem) ctx.addIssue({ code: "custom", path: [k], message: `${def.label}: ${problem}` });
  }
});
var settingOverrideSchema = z6.strictObject({ key: z6.string().max(60), productCode: z6.string().regex(PRODUCT_CODE).nullable().default(null), value: z6.union([z6.number(), z6.string().max(400)]) });
var settingResetSchema = z6.strictObject({ key: z6.string().max(60), productCode: z6.string().regex(PRODUCT_CODE).nullable().default(null) });

// src/packages.ts
var ENTITLEMENT_FEATURES = ["channel.web", "channel.apk", "channel.data_entry", "design.ticket", "design.report", "rules.custom", "domain.custom"];
var ENTITLEMENT_LIMITS = ["products", "agents", "console_users", "domains"];
var FEATURE_LABELS = {
  "channel.web": "Web / PWA agent app",
  "channel.apk": "Android app (APK)",
  "channel.data_entry": "Data entry team",
  "design.ticket": "Own ticket design",
  "design.report": "Own report design",
  "rules.custom": "Own game rules",
  "domain.custom": "Own domain name"
};
var LIMIT_LABELS = {
  products: "Products",
  agents: "Agents",
  console_users: "Console users",
  domains: "Domains"
};
var limitValue = z7.number().int().min(0).max(1e6).nullable();
var packageConfigSchema = z7.strictObject({
  /** every feature listed, on or off (a feature added later reads as off in older versions) */
  features: z7.strictObject(Object.fromEntries(ENTITLEMENT_FEATURES.map((f) => [f, z7.boolean().default(false)]))),
  /** null = no limit (a limit added later reads as 0 in older versions — nothing is given by accident) */
  limits: z7.strictObject(Object.fromEntries(ENTITLEMENT_LIMITS.map((l) => [l, limitValue.default(0)]))),
  /** the catalog products a client of this package may have: 'all' (including products added later) or these codes */
  products: z7.union([z7.literal("all"), z7.array(z7.string().regex(PRODUCT_CODE)).max(50)]),
  /** the package's layer of settings (Phase 6, docs/52): above the platform default, below the template and the company */
  settings: settingsMapSchema.default({})
});
var PACKAGE_CODE = /^[A-Z][A-Z0-9]{1,11}$/;
var PACKAGE_STATUSES = ["active", "archived"];
var packageCreateSchema = z7.strictObject({
  code: z7.string().trim().toUpperCase().regex(PACKAGE_CODE, "2\u201312 letters or digits, starting with a letter"),
  nameEn: z7.string().trim().min(1).max(80),
  nameKm: z7.string().trim().min(1).max(80),
  description: z7.string().trim().max(400).optional(),
  /** version 1 is a draft with this content */
  config: packageConfigSchema
});
var packageUpdateSchema = z7.strictObject({
  nameEn: z7.string().trim().min(1).max(80).optional(),
  nameKm: z7.string().trim().min(1).max(80).optional(),
  description: z7.string().trim().max(400).nullable().optional(),
  status: z7.enum(PACKAGE_STATUSES).optional(),
  price: priceSchema.nullable().optional()
}).refine((v) => Object.keys(v).length > 0, "nothing to change");
var packageVersionUpdateSchema = z7.strictObject({ config: packageConfigSchema, note: z7.string().trim().max(300).optional() });
var packagePublishSchema = z7.strictObject({ confirmCode: z7.string().trim().min(1).max(12) });
var packageMoveClientsSchema = z7.strictObject({ version: z7.number().int().min(1), clientIds: z7.array(z7.number().int().min(1)).min(1).max(1e3).optional() });
var clientPackageSchema = z7.strictObject({ packageCode: z7.string().trim().toUpperCase().regex(PACKAGE_CODE), version: z7.number().int().min(1).optional() });

// src/templates.ts
import { z as z9 } from "zod";

// src/hierarchy.ts
import { z as z8 } from "zod";
var code = z8.string().trim().toUpperCase().regex(/^[A-Z][A-Z0-9_-]{0,19}$/, "1\u201320 letters, digits, - or _, starting with a letter");
var name2 = z8.string().trim().min(1).max(80);
var levelInputSchema = z8.strictObject({
  code,
  nameEn: name2,
  nameKm: name2,
  canSell: z8.boolean().default(false),
  allowedUnder: z8.array(code.nullable()).min(1).max(20)
});
var levelUpdateSchema = z8.strictObject({ nameEn: name2.optional(), nameKm: name2.optional(), canSell: z8.boolean().optional(), allowedUnder: z8.array(code.nullable()).min(1).max(20).optional() }).refine((v) => Object.keys(v).length > 0, "nothing to change");
var areaInputSchema = z8.strictObject({ code, nameEn: name2, nameKm: name2, parentCode: code.nullable().default(null) });
var areaUpdateSchema = z8.strictObject({ nameEn: name2.optional(), nameKm: name2.optional(), parentCode: code.nullable().optional() }).refine((v) => Object.keys(v).length > 0, "nothing to change");
var ACCOUNT_STATUSES = ["active", "disabled"];
var accountCreateSchema = z8.strictObject({ code, name: name2, levelCode: code, parentId: z8.number().int().min(1).nullable(), areaCodes: z8.array(code).max(200).default([]) });
var accountUpdateSchema = z8.strictObject({ code: code.optional(), name: name2.optional(), status: z8.enum(ACCOUNT_STATUSES).optional(), parentId: z8.number().int().min(1).nullable().optional(), areaCodes: z8.array(code).max(200).optional() }).refine((v) => Object.keys(v).length > 0, "nothing to change");
var SHARE_KINDS = ["none", "profit", "winning"];
var SHARE_KIND_LABELS = { none: "No share", profit: "Profit share", winning: "Winning share" };
var pct = z8.number().min(0).max(100);
var dealRuleSchema = z8.strictObject({
  product: z8.union([z8.literal(""), z8.string().regex(PRODUCT_CODE)]).default(""),
  game: z8.union([z8.literal(""), z8.string().regex(/^[A-Z][A-Z0-9]{0,11}$/)]).default(""),
  digits: z8.union([z8.literal(""), z8.literal("2"), z8.literal("3")]).default(""),
  /** % of sales */
  commission: pct,
  /** % of the share basis (profit share: sell − winning − commission; winning share: the winnings, only when there are any) */
  share: pct.default(0)
});
var dealSchema = z8.strictObject({ shareKind: z8.enum(SHARE_KINDS).default("none"), rules: z8.array(dealRuleSchema).max(200) }).superRefine((d, ctx) => {
  const seen = /* @__PURE__ */ new Set();
  d.rules.forEach((r, i) => {
    const k = `${r.product}|${r.game}|${r.digits}`;
    if (seen.has(k)) ctx.addIssue({ code: "custom", path: ["rules", i], message: "the same product / game / digits twice" });
    seen.add(k);
    if (r.game && !r.product) ctx.addIssue({ code: "custom", path: ["rules", i, "game"], message: "a game needs its product" });
    if (d.shareKind === "none" && r.share > 0) ctx.addIssue({ code: "custom", path: ["rules", i, "share"], message: "no share kind chosen" });
  });
});
var statementQuerySchema = z8.object({ from: z8.string().regex(/^\d{4}-\d{2}-\d{2}$/), to: z8.string().regex(/^\d{4}-\d{2}-\d{2}$/) });

// src/templates.ts
var name3 = z9.string().trim().min(1).max(80);
var templateConfigSchema = z9.strictObject({
  /** the products a company created from this template gets installed (Phase 7), each with an optional own name */
  products: z9.array(z9.strictObject({ code: z9.string().regex(PRODUCT_CODE), nameEn: name3.optional(), nameKm: name3.optional() })).max(50).default([]),
  /** the template's layer of settings (between the package and the company's own values) */
  settings: settingsMapSchema.default({}),
  /** the company’s levels (Phase 8): created with the company */
  levels: z9.array(levelInputSchema).max(20).default([])
});
var TEMPLATE_CODE = /^[A-Z][A-Z0-9]{1,11}$/;
var TEMPLATE_STATUSES = ["active", "archived"];
var templateCreateSchema = z9.strictObject({
  code: z9.string().trim().toUpperCase().regex(TEMPLATE_CODE, "2\u201312 letters or digits, starting with a letter"),
  nameEn: name3,
  nameKm: name3,
  description: z9.string().trim().max(400).optional(),
  config: templateConfigSchema
});
var templateUpdateSchema = z9.strictObject({ nameEn: name3.optional(), nameKm: name3.optional(), description: z9.string().trim().max(400).nullable().optional(), status: z9.enum(TEMPLATE_STATUSES).optional() }).refine((v) => Object.keys(v).length > 0, "nothing to change");
var templateVersionUpdateSchema = z9.strictObject({ config: templateConfigSchema, note: z9.string().trim().max(300).optional() });
var templatePublishSchema = z9.strictObject({ confirmCode: z9.string().trim().min(1).max(12) });
var templateMoveClientsSchema = z9.strictObject({ version: z9.number().int().min(1), clientIds: z9.array(z9.number().int().min(1)).min(1).max(1e3).optional() });
var clientTemplateSchema = z9.strictObject({ templateCode: z9.string().trim().toUpperCase().regex(TEMPLATE_CODE).nullable(), version: z9.number().int().min(1).optional() });

// src/permissions.ts
import { z as z10 } from "zod";
var PERMISSION_KEYS = ["accounts", "deals", "tickets", "reports", "products", "settings", "staff"];
var ACCESS_LEVELS = ["none", "read", "edit"];
var accessRank = (a) => ACCESS_LEVELS.indexOf(a);
var PERMISSION_LABELS = {
  accounts: "Accounts, levels and areas",
  deals: "Commission deals",
  tickets: "Tickets (sell, cancel)",
  reports: "Commission statement",
  products: "Products",
  settings: "Company settings",
  staff: "Staff and permissions"
};
var ACCESS_LABELS = { none: "Not allowed", read: "Read only", edit: "Full edit" };
var permissionsSchema = z10.strictObject(Object.fromEntries(PERMISSION_KEYS.map((k) => [k, z10.enum(ACCESS_LEVELS).default("none")])));
var ALL_EDIT = Object.fromEntries(PERMISSION_KEYS.map((k) => [k, "edit"]));
var NONE = Object.fromEntries(PERMISSION_KEYS.map((k) => [k, "none"]));
var COMPANY_ROLES = ["admin", "staff"];
var staffCreateSchema = z10.strictObject({
  username: companyUsernameSchema,
  displayName: z10.string().trim().min(1).max(80),
  position: z10.string().trim().max(60).optional(),
  /** the account the user works for (inside the maker's branch); absent = the maker's own place */
  accountId: z10.number().int().min(1).nullable().optional(),
  permissions: permissionsSchema
});
var staffUpdateSchema = z10.strictObject({ displayName: z10.string().trim().min(1).max(80).optional(), position: z10.string().trim().max(60).nullable().optional(), permissions: permissionsSchema.optional(), status: z10.enum(["active", "disabled"]).optional() }).refine((v) => Object.keys(v).length > 0, "nothing to change");
var companyLoginSchema = z10.strictObject({ company: z10.string().trim().toUpperCase().min(2).max(10), username: z10.string().trim().min(1).max(40), password: z10.string().min(1).max(200) });

// src/domains.ts
import { z as z11 } from "zod";
var DOMAIN_PURPOSES = ["console", "agent", "data_entry"];
var DOMAIN_PURPOSE_LABELS = { console: "Management console", agent: "Agent web app", data_entry: "Data entry" };
var DOMAIN_KINDS = ["subdomain", "custom"];
var DOMAIN_STATUSES = ["pending", "verified", "active", "disabled"];
var hostSchema = z11.string().trim().toLowerCase().max(253).regex(/^(?=.{4,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,24}$/, "a host name like lottery.example.com");
var domainCreateSchema = z11.strictObject({ host: hostSchema, purpose: z11.enum(DOMAIN_PURPOSES).default("console") });
var domainStateSchema = z11.strictObject({ status: z11.enum(["active", "disabled"]).optional(), purpose: z11.enum(DOMAIN_PURPOSES).optional() }).refine((v) => Object.keys(v).length > 0, "nothing to change");
var APPS = ["agent_apk", "agent_pwa", "data_entry_pwa", "console"];
var APP_LABELS = { agent_apk: "Agent app (Android)", agent_pwa: "Agent web app (PWA)", data_entry_pwa: "Data entry (PWA)", console: "Management console" };
var VERSION = /^\d{1,3}\.\d{1,3}\.\d{1,4}$/;
function compareVersions(a, b) {
  const x = a.split(".").map(Number);
  const y = b.split(".").map(Number);
  for (let i = 0; i < 3; i++) if ((x[i] ?? 0) !== (y[i] ?? 0)) return (x[i] ?? 0) - (y[i] ?? 0);
  return 0;
}
var RELEASE_STATUSES = ["draft", "released", "withdrawn"];
var releaseTargetSchema = z11.discriminatedUnion("kind", [
  z11.strictObject({ kind: z11.literal("all") }),
  z11.strictObject({ kind: z11.literal("packages"), codes: z11.array(z11.string().trim().toUpperCase()).min(1).max(50) }),
  z11.strictObject({ kind: z11.literal("clients"), codes: z11.array(z11.string().trim().toUpperCase()).min(1).max(500) })
]);
var releaseCreateSchema = z11.strictObject({
  app: z11.enum(APPS),
  version: z11.string().trim().regex(VERSION, "like 1.2.3"),
  /** devices below this version must update before they go on (null = no minimum) */
  minimumVersion: z11.string().trim().regex(VERSION, "like 1.2.3").nullable().default(null),
  mandatory: z11.boolean().default(false),
  notesEn: z11.string().trim().max(2e3).default(""),
  notesKm: z11.string().trim().max(2e3).default(""),
  /** where the build is (the APK file); empty for web apps, which update themselves */
  url: z11.string().trim().url().max(500).nullable().default(null),
  target: releaseTargetSchema.default({ kind: "all" })
});
var releaseCheckQuery = z11.object({ app: z11.enum(APPS), client: z11.string().trim().toUpperCase().min(2).max(10), current: z11.string().trim().regex(VERSION).optional() });
export {
  ACCESS_LABELS,
  ACCESS_LEVELS,
  ACCOUNT_STATUSES,
  ALL_EDIT,
  APPS,
  APP_LABELS,
  AUDIT_ACTIONS,
  CLIENT_INACTIVE_DAYS,
  CLIENT_PRODUCT_STATUSES,
  CLIENT_STATES,
  CLIENT_TIMEZONES,
  CLIENT_TRANSITIONS,
  COMPANY_ROLES,
  DEFAULT_BETTING,
  DOMAIN_KINDS,
  DOMAIN_PURPOSES,
  DOMAIN_PURPOSE_LABELS,
  DOMAIN_STATUSES,
  DRAW_EXCEPTION_ACTIONS,
  ENTITLEMENT_FEATURES,
  ENTITLEMENT_LIMITS,
  ERROR_CODES,
  FEATURE_LABELS,
  LIMIT_LABELS,
  NONE,
  PACKAGE_CODE,
  PACKAGE_STATUSES,
  PATTERN_CODE,
  PERMISSION_KEYS,
  PERMISSION_LABELS,
  PLATFORM_ROLES,
  PRICE_CURRENCIES,
  PRICE_PERIODS,
  PRODUCT_CODE,
  PRODUCT_STATUSES,
  RELEASE_STATUSES,
  RESULT_SOURCES,
  RESULT_STATES,
  SETTINGS,
  SETTING_SOURCES,
  SHARE_KINDS,
  SHARE_KIND_LABELS,
  TEMPLATE_CODE,
  TEMPLATE_STATUSES,
  TICKET_CHANNELS,
  TICKET_STATUSES,
  VERSION,
  VERSION_STATUSES,
  WEEKDAYS,
  accessRank,
  accountCreateSchema,
  accountUpdateSchema,
  areaInputSchema,
  areaUpdateSchema,
  betLineSchema,
  bettingSchema,
  changePasswordSchema,
  clientCodeSchema,
  clientCreateSchema,
  clientPackageSchema,
  clientPreviewSchema,
  clientPriceSchema,
  clientProductInstallSchema,
  clientProductMoveSchema,
  clientProductRenameSchema,
  clientSlugSchema,
  clientStateSchema,
  clientTemplateSchema,
  clientUpdateSchema,
  closeTimeSchema,
  companyLoginSchema,
  companyUserUpdateSchema,
  companyUsernameSchema,
  compareVersions,
  configGapSchema,
  dealRuleSchema,
  dealSchema,
  domainCreateSchema,
  domainStateSchema,
  drawExceptionSchema,
  drawSchema,
  gameSchema,
  hostSchema,
  levelInputSchema,
  levelUpdateSchema,
  loginSchema,
  packageConfigSchema,
  packageCreateSchema,
  packageMoveClientsSchema,
  packagePublishSchema,
  packageUpdateSchema,
  packageVersionUpdateSchema,
  patternCreateSchema,
  patternTrySchema,
  patternUpdateSchema,
  permissionsSchema,
  priceSchema,
  productConfigSchema,
  productPublishSchema,
  productStatusSchema,
  productUpdateSchema,
  productVersionUpdateSchema,
  refreshSchema,
  releaseCheckQuery,
  releaseCreateSchema,
  releaseTargetSchema,
  resultAmendSchema,
  resultInputSchema,
  resultPasteSchema,
  resultSaveSchema,
  scheduleSchema,
  sellSchema,
  settingDef,
  settingOverrideSchema,
  settingProblem,
  settingResetSchema,
  settingsMapSchema,
  slipSchema,
  splitSettingKey,
  staffCreateSchema,
  staffUpdateSchema,
  statementQuerySchema,
  templateConfigSchema,
  templateCreateSchema,
  templateMoveClientsSchema,
  templatePublishSchema,
  templateUpdateSchema,
  templateVersionUpdateSchema,
  ticketCancelSchema,
  ticketListQuery
};
//# sourceMappingURL=index.js.map